import { useMemo, useState } from "react";
import { Calculator, Zap, IndianRupee, TrendingUp, Sun, ArrowRight } from "lucide-react";

/**
 * Karnataka (BESCOM) assumptions — kept transparent for the customer.
 * - Average effective residential tariff: ₹7 / unit (post-slab average)
 * - Solar yield in Karnataka: ~4 kWh per kW per day = ~120 units / kW / month
 * - System cost (residential, post-subsidy via PM Surya Ghar): ~₹60,000 / kW
 *   (above 10 kW commercial / industrial, ~₹55,000 / kW without subsidy)
 * - Annual bill offset: 90% (cloudy days + maintenance window)
 * - System lifetime: 25 years (matches Tier-1 panel warranty)
 */
const TARIFF_BY_TYPE = {
  Residential: 7,
  Commercial: 9,
  Industrial: 8.5,
};
const UNITS_PER_KW_MONTH = 120;
const COST_PER_KW = {
  Residential: 60000,
  Commercial: 55000,
  Industrial: 52000,
};
const OFFSET_FACTOR = 0.9;
const LIFETIME_YEARS = 25;

const formatINR = (n) => {
  if (!isFinite(n) || n <= 0) return "—";
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)} Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(2)} L`;
  if (n >= 1000) return `₹${(n / 1000).toFixed(1)} K`;
  return `₹${Math.round(n)}`;
};

const SIZE_TO_SELECT = (kw) => {
  if (kw < 5) return "Below 5 kW";
  if (kw < 10) return "5 – 10 kW";
  if (kw < 25) return "10 – 25 kW";
  if (kw < 100) return "25 – 100 kW";
  return "Above 100 kW";
};

export default function SavingsCalculator() {
  const [bill, setBill] = useState(5000);
  const [type, setType] = useState("Residential");

  const result = useMemo(() => {
    const tariff = TARIFF_BY_TYPE[type];
    const monthlyUnits = bill / tariff;
    const recommendedKw = Math.max(0, monthlyUnits / UNITS_PER_KW_MONTH);
    const annualSavings = bill * 12 * OFFSET_FACTOR;
    const systemCost = recommendedKw * COST_PER_KW[type];
    const payback = annualSavings > 0 ? systemCost / annualSavings : 0;
    const lifetimeSavings = annualSavings * LIFETIME_YEARS - systemCost;
    return {
      recommendedKw,
      monthlyUnits,
      annualSavings,
      systemCost,
      payback,
      lifetimeSavings,
    };
  }, [bill, type]);

  const goToEnquiry = () => {
    const select = document.querySelector('[data-testid="input-size"]');
    if (select) {
      const sizeStr = SIZE_TO_SELECT(result.recommendedKw);
      const nativeSetter = Object.getOwnPropertyDescriptor(
        window.HTMLSelectElement.prototype,
        "value"
      ).set;
      nativeSetter.call(select, sizeStr);
      select.dispatchEvent(new Event("change", { bubbles: true }));

      const propType = document.querySelector('[data-testid="input-property"]');
      if (propType) {
        nativeSetter.call(propType, type);
        propType.dispatchEvent(new Event("change", { bubbles: true }));
      }
    }
    document
      .getElementById("enquiry")
      ?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="savings"
      data-testid="savings-section"
      className="section-pad relative overflow-hidden"
    >
      <div className="absolute inset-0 -z-10 opacity-40 pointer-events-none">
        <div className="absolute top-10 right-1/4 h-[420px] w-[420px] sun-ring" />
      </div>

      <div className="container-x">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          {/* Left — pitch */}
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.3em] text-[#FFC857] mb-6">
              — Savings Calculator
            </div>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-white font-light leading-[1.1]">
              See your savings,{" "}
              <span className="italic text-[#FFC857]">before you commit.</span>
            </h2>
            <p className="mt-6 text-[#A0ABC0] text-base md:text-lg font-light leading-relaxed">
              A two-input estimator calibrated to Karnataka tariffs and PM
              Surya Ghar subsidies. Numbers are indicative — your site visit
              will return an exact quote.
            </p>

            <div className="mt-10 space-y-4 text-sm text-[#A0ABC0] font-light">
              <Bullet>
                BESCOM-calibrated tariff (₹{TARIFF_BY_TYPE[type]}/unit ·{" "}
                {type})
              </Bullet>
              <Bullet>
                Karnataka solar yield: ~{UNITS_PER_KW_MONTH} units / kW / month
              </Bullet>
              <Bullet>
                System cost: ~₹
                {(COST_PER_KW[type] / 1000).toFixed(0)}K per kW (post-subsidy)
              </Bullet>
              <Bullet>25-year panel warranty, 10-year inverter warranty</Bullet>
            </div>
          </div>

          {/* Right — calculator */}
          <div className="lg:col-span-7">
            <div className="glass rounded-sm p-7 md:p-9">
              <div className="flex items-center gap-3 mb-7">
                <span className="h-11 w-11 rounded-full bg-gradient-to-br from-[#FFC857] to-[#F5A623] flex items-center justify-center">
                  <Calculator className="h-5 w-5 text-[#050B14]" />
                </span>
                <div>
                  <div className="font-serif text-xl text-white">
                    Quick Estimate
                  </div>
                  <div className="text-xs uppercase tracking-[0.22em] text-[#A0ABC0] mt-0.5">
                    Drag to adjust · live update
                  </div>
                </div>
              </div>

              {/* Property type pills */}
              <div className="mb-7">
                <div className="text-[11px] uppercase tracking-[0.22em] text-[#A0ABC0] mb-3">
                  Property Type
                </div>
                <div className="flex gap-2 flex-wrap">
                  {Object.keys(TARIFF_BY_TYPE).map((t) => (
                    <button
                      key={t}
                      data-testid={`calc-type-${t.toLowerCase()}`}
                      type="button"
                      onClick={() => setType(t)}
                      className={`px-5 py-2.5 rounded-full text-sm transition-all border ${
                        type === t
                          ? "bg-[#FFC857] text-[#050B14] border-[#FFC857] font-medium"
                          : "bg-transparent text-[#A0ABC0] border-white/15 hover:border-[#FFC857]/50 hover:text-white"
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>

              {/* Bill slider */}
              <div className="mb-8">
                <div className="flex items-baseline justify-between mb-3">
                  <span className="text-[11px] uppercase tracking-[0.22em] text-[#A0ABC0]">
                    Monthly electricity bill
                  </span>
                  <span className="font-serif text-2xl text-[#FFC857]">
                    ₹{bill.toLocaleString("en-IN")}
                  </span>
                </div>
                <input
                  data-testid="calc-bill-slider"
                  type="range"
                  min={500}
                  max={500000}
                  step={500}
                  value={bill}
                  onChange={(e) => setBill(Number(e.target.value))}
                  className="w-full accent-[#FFC857] cursor-pointer"
                />
                <div className="flex justify-between text-[10px] uppercase tracking-widest text-[#A0ABC0]/60 mt-2">
                  <span>₹500</span>
                  <span>₹5 L</span>
                </div>

                <div className="mt-4 flex items-center gap-2">
                  <span className="text-xs text-[#A0ABC0]">
                    Or type your bill:
                  </span>
                  <div className="flex-1 relative">
                    <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#A0ABC0]" />
                    <input
                      data-testid="calc-bill-input"
                      type="number"
                      min={0}
                      value={bill || ""}
                      onChange={(e) =>
                        setBill(Math.max(0, Number(e.target.value)))
                      }
                      className="w-full bg-[#050B14]/60 border border-white/10 focus:border-[#FFC857]/60 focus:outline-none rounded-sm pl-8 pr-3 py-2 text-white text-sm font-light"
                    />
                  </div>
                </div>
              </div>

              {/* Result grid */}
              <div className="grid grid-cols-2 gap-3 md:gap-4">
                <Stat
                  testId="calc-kw"
                  icon={Zap}
                  label="Recommended size"
                  value={`${result.recommendedKw.toFixed(1)} kW`}
                />
                <Stat
                  testId="calc-annual"
                  icon={TrendingUp}
                  label="Annual savings"
                  value={formatINR(result.annualSavings)}
                />
                <Stat
                  testId="calc-payback"
                  icon={Sun}
                  label="Payback period"
                  value={
                    result.payback > 0
                      ? `${result.payback.toFixed(1)} yrs`
                      : "—"
                  }
                />
                <Stat
                  testId="calc-lifetime"
                  icon={IndianRupee}
                  label="25-year net savings"
                  value={formatINR(result.lifetimeSavings)}
                  highlight
                />
              </div>

              <div className="mt-7 flex items-center justify-between text-xs text-[#A0ABC0]/70 font-light">
                <span>Est. system cost: {formatINR(result.systemCost)}</span>
                <span>≈ {Math.round(result.monthlyUnits)} units / month</span>
              </div>

              <button
                data-testid="calc-cta"
                onClick={goToEnquiry}
                className="btn-gold mt-7 w-full justify-center"
              >
                Get exact quote for {result.recommendedKw.toFixed(1)} kW
                <ArrowRight className="h-4 w-4" />
              </button>

              <p className="mt-4 text-[11px] text-[#A0ABC0]/70 font-light text-center">
                Indicative numbers based on Karnataka averages. Final quote
                depends on roof orientation, shading & connection load.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Bullet({ children }) {
  return (
    <div className="flex items-start gap-3">
      <span className="h-1 w-6 bg-[#FFC857] mt-2 shrink-0" />
      <span>{children}</span>
    </div>
  );
}

function Stat({ icon: Icon, label, value, highlight, testId }) {
  return (
    <div
      data-testid={testId}
      className={`rounded-sm p-4 md:p-5 border ${
        highlight
          ? "border-[#FFC857]/40 bg-gradient-to-br from-[#FFC857]/12 to-transparent"
          : "border-white/10 bg-white/[0.02]"
      }`}
    >
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#A0ABC0]">
        <Icon className="h-3.5 w-3.5 text-[#FFC857]" />
        <span className="truncate">{label}</span>
      </div>
      <div
        className={`mt-2 font-serif font-light leading-none ${
          highlight ? "text-3xl md:text-4xl text-[#FFC857]" : "text-2xl md:text-3xl text-white"
        }`}
      >
        {value}
      </div>
    </div>
  );
}
